import { MenuItem, Select, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

/** A legal form as `GET /company/legal-forms` lists it: its id and its names per language. */
export interface LegalFormChoice {
  id: string;
  names: Partial<Record<string, { short: string; long: string }>>;
}

// The UI language's names, else German: the forms are Swiss and German is the primary UI language.
const namesOf = (choice: LegalFormChoice, language: string) =>
  choice.names[language] ?? choice.names[language.split('-')[0]] ?? choice.names.de;

/** The field's face: the short name, as drawn (Figma `40656:96151` reads "AG"). */
export const legalFormShortName = (choice: LegalFormChoice, language: string) =>
  namesOf(choice, language)?.short ?? choice.id;

/** A menu row: the long name with its short one, said once where they are the same word. */
export const legalFormMenuLabel = (choice: LegalFormChoice, language: string) => {
  const names = namesOf(choice, language);
  if (!names) return choice.id;
  return names.long === names.short ? names.long : `${names.long} (${names.short})`;
};

export interface LegalFormSelectProps {
  options: readonly LegalFormChoice[];
  /** The form's id; null while there is none. */
  value: string | null;
  /** Shows the form without a menu: the register gave it, or this caller may not change it. */
  readOnly?: boolean;
  disabled?: boolean;
  onChange?: (legalForm: string) => void;
  placeholder?: string;
  /** Read-only text where `value` names no option, e.g. the register's own name for a form the platform lacks. */
  fallbackText?: string;
  /** The id of the row's label, which names the field. */
  labelId?: string;
  'data-testid'?: string;
}

/**
 * The Legal form field of the company forms. Read-only it is the drawn `Dropdown [upd]`
 * read-only state: the theme's read-only fill, no chevron, no menu. Otherwise a select over the
 * server's forms; the caller decides when a pick is allowed and what it writes.
 */
export function LegalFormSelect({
  options,
  value,
  readOnly = false,
  disabled = false,
  onChange,
  placeholder,
  fallbackText,
  labelId,
  'data-testid': testId,
}: LegalFormSelectProps) {
  const { i18n } = useTranslation();
  const selected = options.find((option) => option.id === value);
  // Before the options load (or where they failed), the id stands in: the ids are the forms'
  // German short names, so a stored form never reads as none.
  const face = selected
    ? legalFormShortName(selected, i18n.language)
    : (fallbackText ?? value ?? '');

  if (readOnly) {
    return (
      <TextField
        fullWidth
        value={face}
        slotProps={{ input: { readOnly: true }, htmlInput: { 'aria-labelledby': labelId } }}
        data-testid={testId}
      />
    );
  }

  return (
    <Select<string>
      fullWidth
      displayEmpty
      labelId={labelId}
      // An id no option carries yet stays out of the Select's value, which MUI would warn about.
      value={selected ? selected.id : ''}
      disabled={disabled}
      onChange={(event) => onChange?.(event.target.value)}
      renderValue={() => face || placeholder}
      data-testid={testId}
    >
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          {legalFormMenuLabel(option, i18n.language)}
        </MenuItem>
      ))}
    </Select>
  );
}
